import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  ShoppingCart,
  Store,
  UtensilsCrossed,
  Croissant,
  Coffee,
  Pizza,
  Utensils,
  MapPin,
  Wallet,
  PiggyBank,
  Ticket,
  Sailboat,
  Sprout,
  Waves,
  Crown,
  Shield,
  Church,
  Landmark,
  Trees,
  Leaf,
  Mountain,
  ShoppingBag,
  Footprints,
  BedDouble,
  BookOpen,
  Castle,
  Eye,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Reveal } from "@/components/motion";
import { Seo } from "@/components/Seo";
import { ShareButton } from "@/components/ShareButton";
import { ShareBar } from "@/components/ShareBar";
import { ExploreLinks } from "@/components/ExploreLinks";
import { GygTourBand } from "@/components/GetYourGuide";
import { ACCOMMODATION } from "@/data/copenhagen";

const SITE = "https://cphhighlights.com";

const HOSTELS = ACCOMMODATION.find((c) => c.id === "hostels").places;

const HERO_IMG =
  "https://images.unsplash.com/photo-1690283113237-54159db1ddca?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const BUN_IMG =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/a76mcsem_2026-08-19%2002_23_01.714%2B0200.webp";

const TIPS = [
  {
    icon: ShoppingCart,
    title: "Shop the supermarkets",
    body:
      "Buy most of your food and drinks from a supermarket — Netto, 365discount, Rema 1000 or Føtex. Grab a take-away salad or sandwich, or, if there's a microwave where you're staying, some ready-to-heat meals to warm up.",
  },
  {
    icon: Store,
    title: "7-Eleven does the job",
    body:
      "Grabbing food from 7-Eleven is cheaper than eating out. They keep a selection of warm foods on the go, plus plenty of sandwiches and salads.",
  },
  {
    icon: UtensilsCrossed,
    title: "Street-food markets",
    body:
      "Eating at a street-food market like Reffen or Broens Gadekøkken is much cheaper than a sit-down restaurant. Just note the drinks won't be as cheap as a supermarket's.",
  },
  {
    icon: Croissant,
    title: "Fill up at a bakery",
    body:
      "Bakery goods cost a fraction of a restaurant meal. Treat yourself to a proper cinnamon bun (kanelsnegl) and you've got a very Danish, very cheap breakfast.",
    link: "/bakeries",
    linkLabel: "See our bakery guide",
  },
  {
    icon: Coffee,
    title: "Coffee to go at McDonald's",
    body:
      "Buy your take-away coffee at McDonald's — it's genuinely good and cheaper than any café in town.",
  },
];

const PLACES = [
  {
    icon: Pizza,
    tag: "Cheap pizza",
    name: "Mio",
    area: "Frederiksberg & Østerbro",
    body: "For a cheap but genuinely good pizza, Mio is the local go-to.",
    addresses: [
      { street: "Falkoner Allé 17", locality: "Frederiksberg" },
      { street: "Nordre Frihavnsgade 54", locality: "Copenhagen" },
    ],
  },
  {
    icon: Utensils,
    tag: "Fair-priced",
    name: "Le Petit Rouge",
    area: "Indre By",
    body: "A fair-priced sit-down spot that won't blow the budget completely.",
    addresses: [{ street: "Kristen Bernikows Gade 9", locality: "Copenhagen" }],
  },
  {
    icon: Utensils,
    tag: "Fair-priced",
    name: "Bouillon",
    area: "Frederiksberg",
    body: "Honest, fairly-priced cooking over on Frederiksberg.",
    addresses: [{ street: "Solbjergvej 3", locality: "Frederiksberg" }],
  },
];

const FREE = [
  { icon: Sailboat, title: "Stroll Nyhavn", body: "Wander the postcard harbour — coloured townhouses, old wooden ships and buzzing quaysides." },
  { icon: Sprout, title: "Wander into Christiania", body: "Explore the free-spirited, self-governed neighbourhood of murals, workshops and lakeside paths." },
  { icon: Waves, title: "Swim the harbour", body: "Dive in at Sandkaj, Islands Brygge or Kalvebod Bølge — clean water, and very Copenhagen." },
  { icon: Crown, title: "Changing of the Guard", body: "Catch the Royal Guards march to Amalienborg for the changing of the guard at 12:00." },
  { icon: Shield, title: "Little Mermaid & Kastellet", body: "See the famous statue, then walk the grassy star-shaped ramparts of Kastellet fortress." },
  { icon: Church, title: "Step inside Marmorkirken", body: "The Marble Church, with the largest dome in Scandinavia — free entry." },
  { icon: Landmark, title: "Climb Christiansborg Tower", body: "Ride up the city's tallest tower for a sweeping rooftop view — completely free." },
  { icon: BookOpen, title: "The Black Diamond", body: "The Royal Library's black-marble harbour extension — reading rooms, exhibitions and big harbour windows, all free." },
  { icon: Castle, title: "The National Museum", body: "From Vikings to modern Denmark inside a grand old prince's palace — the permanent collection is free." },
  { icon: Trees, title: "Kongens Have & Botanical Garden", body: "Picnic in the King's Garden, then wander the free grounds of the Botanical Garden." },
  { icon: Leaf, title: "Cemetery parks", body: "Walk leafy Assistens Cemetery, where H.C. Andersen rests, and the beautiful Bispebjerg Cemetery." },
  { icon: Mountain, title: "CopenHill hike & Nordhavn views", body: "Hike up CopenHill, the ski-slope power plant, or head up Konditaget Lüders in Nordhavn." },
  { icon: ShoppingBag, title: "Window-shop Strøget", body: "Stroll one of Europe's longest pedestrian streets — browsing is free." },
  { icon: Eye, title: "Browse Magasin & Illum", body: "Copenhagen's grand department stores — Magasin du Nord on Kongens Nytorv and high-end Illum on Strøget. Looking costs nothing." },
  { icon: Store, title: "Browse Torvehallerne", body: "Soak up the glass-covered food market — free to wander even if you don't buy." },
  { icon: Footprints, title: "Neighbourhood strolls", body: "Lose an afternoon in the Latin Quarter, Vesterbro, Østerbro or Nørrebro." },
];

const mapUrl = (name, a) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${name}, ${a.street}, ${a.locality}, Denmark`
  )}`;

const BREADCRUMB = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Copenhagen on a Budget", item: `${SITE}/budget` },
  ],
};

const WEBPAGE = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Copenhagen on a Budget: Prices, Free Things & Money-Saving Tips",
  url: `${SITE}/budget`,
  description:
    "How to travel on a budget in Copenhagen. A guide to budget travel in Copenhagen. Eat cheap guide. The best free things to do in Copenhagen. Travel on a budget to Copenhagen without missing out.",
  isPartOf: { "@type": "WebSite", name: "CPH Highlights", url: `${SITE}/` },
  publisher: { "@type": "Organization", name: "CPH Highlights", url: `${SITE}/` },
};

const PLACES_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Cheap & fair-priced places to eat in Copenhagen",
  itemListElement: PLACES.flatMap((p) => p.addresses.map((a) => ({ p, a }))).map(
    ({ p, a }, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Restaurant",
        name: `${p.name} (${a.locality})`,
        ...(p.tag === "Cheap pizza" ? { servesCuisine: "Pizza" } : {}),
        priceRange: "$",
        hasMap: mapUrl(p.name, a),
        address: {
          "@type": "PostalAddress",
          streetAddress: a.street,
          addressLocality: a.locality,
          addressCountry: "DK",
        },
      },
    })
  ),
};

export default function BudgetPage() {
  const scrollToFree = () => {
    const el = document.getElementById("free-things");
    if (!el) return;
    window.history.replaceState(null, "", "#free-things");
    if (window.__lenis) {
      window.__lenis.resize();
      window.__lenis.scrollTo(el, { offset: -70 });
    } else {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };
  return (
    <main data-testid="budget-page" className="pt-16 md:pt-20">
      <Seo
        title="Copenhagen on a Budget: Prices, Free Things & Money-Saving Tips"
        description="Copenhagen on a budget: real prices, the best free things to do, cheap eats from bakeries and food halls, City Pass math and local money-saving tips."
        path="/budget"
        image={HERO_IMG}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(BREADCRUMB)}</script>
        <script type="application/ld+json">{JSON.stringify(WEBPAGE)}</script>
        <script type="application/ld+json">{JSON.stringify(PLACES_SCHEMA)}</script>
      </Helmet>

      {/* hero */}
      <section className="relative bg-[#13141A] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Casual, affordable dining along a Copenhagen street"
            className="w-full h-full object-cover opacity-35"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#13141A] via-[#13141A]/70 to-[#13141A]/40" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-28">
          <div className="flex items-center gap-3 mb-8">
            <Link
              to="/"
              data-testid="budget-back-home"
              className="inline-flex items-center gap-2 font-body font-700 text-sm bg-black/40 text-white px-5 py-2.5 rounded-full border border-white/20 hover:bg-white hover:text-black transition-colors backdrop-blur-sm"
            >
              <ArrowLeft size={16} /> Back to start
            </Link>
            <ShareButton testid="budget-share" />
          </div>
          <div className="flex items-center gap-3 mb-4">
            <p className="font-body font-800 text-xs uppercase tracking-[0.3em] eyebrow text-white">
              THE LOW-BUDGET GUIDE
            </p>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-900 tracking-tighter leading-[0.85] text-4xl sm:text-5xl lg:text-6xl"
          >
            Copenhagen<br />
            <span className="font-editorial italic font-600">on a budget</span>
          </motion.h1>
          <p className="font-body text-base md:text-lg text-white/90 max-w-2xl mt-6 leading-relaxed">
            Copenhagen is famous for its food scene — but a trip to Denmark&apos;s capital doesn&apos;t have
            to mean fancy gourmet restaurants. With a few local tricks, you can eat well here without
            spending a fortune.
          </p>
          <p data-testid="budget-updated" className="font-body text-xs uppercase tracking-[0.2em] text-white/40 mt-4">
            Last updated: 29 September 2026
          </p>
          <button
            type="button"
            onClick={scrollToFree}
            data-testid="budget-hero-free-btn"
            className="inline-flex items-center gap-2 font-display font-900 text-base bg-[#DF3226] text-white px-6 py-3.5 rounded-full mt-8 border border-black/10 shadow-brut hover:-translate-y-0.5 hover:shadow-xl transition-all"
          >
            <Ticket size={18} /> Free things to do
          </button>
        </div>
      </section>

      {/* eat cheap tips */}
      <section className="bg-[#F9F8F6]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <Reveal>
            <div className="flex items-center gap-3 mb-3">
              <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">
                HOW TO EAT CHEAP
              </p>
            </div>
            <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl max-w-3xl">
              Skip the fancy spots,<br />
              <span className="font-editorial italic font-600">here&apos;s a guide to survive on a budget</span>
            </h2>
            <p className="font-body text-base md:text-lg text-black/70 mt-6 max-w-2xl leading-relaxed">
              The trick is to stay away from fancy restaurants and lean on supermarkets, bakeries and
              casual spots.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
            {TIPS.map((t, i) => (
              <Reveal key={t.title} delay={i * 0.05} y={30}>
                <div
                  data-testid={`budget-tip-${i}`}
                  className="bg-white border border-zinc-200 shadow-brut p-5 h-full flex items-start gap-4 hover:-translate-y-1 hover:shadow-brut-lg transition-all"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-[#13141A] text-white shrink-0">
                    <t.icon size={19} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display font-900 tracking-tight text-lg leading-tight">
                      {t.title}
                    </h3>
                    <p className="font-body text-sm text-black/70 mt-1.5 leading-relaxed">
                      {t.body}
                    </p>
                    {t.link && (
                      <Link
                        to={t.link}
                        data-testid={`budget-tip-link-${i}`}
                        className="inline-flex items-center gap-1.5 font-body font-800 text-sm text-[#DF3226] mt-3 hover:gap-2.5 transition-all"
                      >
                        {t.linkLabel}
                      </Link>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* named cheap eats */}
      <section className="bg-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5 lg:sticky lg:top-28">
              <Reveal>
                <div className="flex items-center gap-3 mb-3">
                  <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">
                    CHEAP EATS WORTH SEEKING OUT
                  </p>
                </div>
                <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl">
                  A few names<br />
                  <span className="font-editorial italic font-600">to remember</span>
                </h2>
                <p className="font-body text-base md:text-lg text-black/70 mt-6 max-w-md leading-relaxed">
                  A cheap but genuinely good pizza, and a couple of fair-priced restaurants that
                  won&apos;t wreck the budget.
                </p>
                <div className="mt-8 border border-zinc-200 shadow-brut overflow-hidden aspect-[4/3]">
                  <img
                    src={BUN_IMG}
                    alt="A fair-priced Copenhagen restaurant dish — steak in gravy with creamy potatoes"
                    className="w-full h-full object-cover"
                  />
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-7 space-y-4">
              {PLACES.map((p, i) => (
                <Reveal key={`${p.name}-${p.area}`} delay={i * 0.05} y={30}>
                  <div
                    data-testid={`budget-place-${i}`}
                    className="bg-[#F9F8F6] border border-zinc-200 shadow-brut p-5 flex items-start gap-4"
                  >
                    <div className="w-10 h-10 flex items-center justify-center bg-[#DF3226] text-white shrink-0">
                      <p.icon size={19} />
                    </div>
                    <div className="flex-1">
                      <span className="inline-block font-body font-800 text-[10px] tracking-widest text-[#DF3226] mb-1">
                        {p.tag.toUpperCase()}
                      </span>
                      <h3 className="font-display font-900 tracking-tight text-lg leading-tight">
                        {p.name} <span className="text-black/40 font-editorial italic font-600 text-base">· {p.area}</span>
                      </h3>
                      <p className="font-body text-sm text-black/70 mt-1.5 leading-relaxed">
                        {p.body}
                      </p>
                      <div className="mt-2.5 flex flex-col gap-1.5">
                        {p.addresses.map((a) => (
                          <a
                            key={a.street}
                            href={mapUrl(p.name, a)}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`budget-place-${i}-map-${a.street.replace(/\s+/g, "-")}`}
                            className="inline-flex items-center gap-1.5 font-body font-700 text-sm text-black/70 hover:text-[#DF3226] transition-colors w-fit"
                          >
                            <MapPin size={14} className="text-[#DF3226] shrink-0" />
                            <span className="underline decoration-black/20 underline-offset-2 hover:decoration-[#DF3226]">
                              {a.street}
                            </span>
                            <span className="text-black/40">· {a.locality}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* free things to do */}
      <section id="free-things" className="bg-[#13141A] text-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <Reveal>
            <div className="flex items-center gap-3 mb-3">
              <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">
                THE BEST THINGS ARE FREE
              </p>
            </div>
            <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl max-w-3xl">
              Free things to do<br />
              <span className="font-editorial italic font-600">in Copenhagen</span>
            </h2>
            <p className="font-body text-base md:text-lg text-white/80 mt-6 max-w-2xl leading-relaxed">
              You don&apos;t need to spend a krone to fall for Copenhagen. Here are the best free ways to
              soak up the city.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {FREE.map((f, i) => (
              <Reveal key={f.title} delay={i * 0.04} y={30}>
                <div
                  data-testid={`budget-free-${i}`}
                  className="bg-white/[0.06] border border-white/15 p-6 h-full flex items-start gap-4 rounded-2xl hover:bg-white/[0.1] transition-colors"
                >
                  <div className="w-11 h-11 flex items-center justify-center bg-[#DF3226] text-white shrink-0 rounded-xl">
                    <f.icon size={20} />
                  </div>
                  <div>
                    <h3 className="font-display font-900 tracking-tight text-xl leading-tight">
                      {f.title}
                    </h3>
                    <p className="font-body text-sm text-white/70 mt-1.5 leading-relaxed">
                      {f.body}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* getting around cheap */}
      <section data-testid="budget-transport" className="bg-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-20">
          <Reveal>
            <div className="flex items-center gap-3 mb-3">
              <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">GETTING AROUND</p>
            </div>
            <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl max-w-3xl">
              Walk first. <span className="font-editorial italic font-600">It's free</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 gap-5 mt-10">
            <Reveal y={30}>
              <div data-testid="budget-transport-walk" className="bg-[#F9F8F6] border border-zinc-200 shadow-brut p-6 md:p-7 h-full">
                <h3 className="font-display font-900 tracking-tight text-xl">Walk — or bike cheap</h3>
                <p className="font-body text-sm md:text-base text-black/70 mt-3 leading-relaxed max-w-2xl">
                  Copenhagen is small and flat — most must-sees sit within a 30-minute stroll of each
                  other, and walking is half the experience. For longer hops, the Metro is the cheap
                  and easy choice — a single ticket covers the city.
                </p>
                <Link
                  to="/transport"
                  data-testid="budget-transport-link"
                  className="group inline-flex items-center gap-2 font-body font-700 text-sm text-[#DF3226] mt-4"
                >
                  Tickets, passes and getting around <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* sleep cheap */}
      <section data-testid="budget-sleep" className="bg-[#F9F8F6] border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-20">
          <Reveal>
            <div className="flex items-center gap-3 mb-3">
              <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">SLEEP CHEAP</p>
            </div>
            <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl max-w-3xl">
              A bed from <span className="font-editorial italic font-600">~450 DKK a night</span>
            </h2>
            <p className="font-body text-base md:text-lg text-black/70 mt-5 max-w-2xl leading-relaxed">
              Copenhagen&apos;s design hostels punch way above their price — pools, cinemas and pod beds
              included. These are the four we point friends to:
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
            {HOSTELS.map((h, i) => (
              <Reveal key={h.name} delay={i * 0.05} y={30}>
                <div
                  data-testid={`budget-hostel-${i}`}
                  className="bg-white border border-zinc-200 shadow-brut p-6 h-full"
                >
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-display font-900 tracking-tight text-xl leading-tight">{h.name}</h3>
                    <span className="font-body font-800 text-sm text-[#DF3226] whitespace-nowrap">{h.price}</span>
                  </div>
                  <p className="font-body font-800 text-[11px] tracking-widest text-black/40 mt-1 uppercase">
                    {h.area}
                  </p>
                  <p className="font-body text-sm md:text-base text-black/70 mt-2 leading-relaxed">{h.note}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <Link
              to="/stay"
              data-testid="budget-sleep-link"
              className="group inline-flex items-center gap-2 font-body font-700 text-base text-black/80 hover:text-[#DF3226] transition-colors mt-8"
            >
              <ArrowRight size={18} className="text-[#DF3226] group-hover:translate-x-1 transition-transform" />
              See all 20 places to stay, sorted by budget
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#13141A] text-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24 text-center md:text-left flex flex-col md:flex-row items-center justify-between gap-8">
          <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl">
            Planning the days?<br />
            <span className="font-editorial italic font-600">Keep it easy on the wallet</span>
          </h2>
          <Link
            to="/planner"
            data-testid="budget-to-planner"
            className="inline-flex items-center gap-2 font-display font-900 text-lg bg-[#DF3226] text-white px-8 py-4 rounded-full shadow-brut hover:-translate-y-1 hover:shadow-brut-lg transition-all duration-300"
          >
            <Wallet size={18} /> Open the planner
          </Link>
        </div>
      </section>

      <GygTourBand tourId="37848" label="THE CHEAPEST CANAL TOUR IN TOWN — FROM GAMMEL STRAND" />
      <ExploreLinks
        testid="explore-budget"
        links={[
          { label: "Free days out — things to do in Copenhagen", to: "/things-to-do" },
          { label: "Getting around for less — the transport guide", to: "/transport" },
          { label: "Family trip? Copenhagen with kids — much of it free", to: "/copenhagen-with-kids" },
        ]}
      />
      <ShareBar />
    </main>
  );
}
